import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EncryptionService } from './encryption.service';

export interface NotificationData {
  id: string;
  type: string;
  title: string;
  message: string;
  entity_type: string;
  entity_id: string;
  created_at: string;
  is_read: boolean;
}

export interface NotificationResponse {
  status: boolean;
  message: string;
  type: string;
  error_status: {
    error_code: string;
  };
  data: {
    notifications: NotificationData[];
    webhook_url: string;
    pagination?: {
      page: number;
      page_size: number;
      total: number;
    };
    unread_count?: number;
    read_count?: number;
  };
}

export interface NotificationCountResponse {
  status: boolean;
  message: string;
  type: string;
  error_status: {
    error_code: string;
  };
  data: {
    unread_count: number;
  };
}

export interface MarkReadRequest {
  notification_ids: string[];
}

export interface MarkReadResponse {
  status: boolean;
  message: string;
  type: string;
  error_status: {
    error_code: string;
  };
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private readonly notificationsSubject = new BehaviorSubject<NotificationData[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  // Centralized polling management
  private notificationsPollingSubscription: Subscription | null = null;
  private countPollingSubscription: Subscription | null = null;
  private isPollingActive: boolean = false;
  private readonly pollingInterval: number = 10000; // 10 seconds

  constructor(
    private readonly http: HttpClient,
    private readonly encryptionService: EncryptionService
  ) {
    this.setupVisibilityChangeListener();
  }

  /**
   * Get user ID from encrypted localStorage
   */
  private async getUserId(): Promise<string | null> {
    try {
      const encryptedUserInfo = localStorage.getItem('user_info');
      if (!encryptedUserInfo) {
        console.error('No user info found in localStorage');
        return null;
      }

      // First try to parse as JSON (for mock user)
      try {
        const mockUser = JSON.parse(encryptedUserInfo);
        if ( mockUser?.id) {
          return mockUser.id;
        }
      } catch (jsonError) {
        console.error('Not a JSON user, trying to decrypt...');
      }

      // If not JSON, try to decrypt
      const userInfo = await this.encryptionService.decrypt(encryptedUserInfo);
      if ( userInfo?.id) {
        return userInfo.id;
      } else {
        console.error('User ID not found in decrypted user info');
        return null;
      }
    } catch (error) {
      console.error('Error getting user ID:', error);
      return null;
    }
  }

  /**
   * Get all notifications for the current user
   * @param showLoading - Whether to show loading state (default: true for manual calls, false for polling)
   * @param page - Page number to fetch (default: 1)
   * @param pageSize - Number of items per page (default: 50)
   * @param status - Filter by notification status (optional: 'read', 'unread')
   */
  async getNotifications(showLoading: boolean = true, page: number = 1, pageSize: number = 50, status?: string): Promise<Observable<NotificationResponse>> {
    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('User ID not found');
    }

    const url = `${environment.apiBaseUrl}${environment.endpoints.notifications_get.replace('{user_id}', userId)}`;
    
    // Add pagination parameters and status if provided
    const params: any = { page: page.toString(), page_size: pageSize.toString() };
    
    // Add status parameter if provided
    if (status) {
      params.status = status;
    }

    // Only log for manual calls, not for polling to reduce console noise
    if (showLoading) {
      console.error('Getting notifications from:', url, 'with params:', params);
    }

    return this.http.get<NotificationResponse>(url, { params });
  }

  /**
   * Get unread notification count
   * @param showLoading - Whether to show loading state (default: true for manual calls, false for polling)
   */
  async getUnreadCount(showLoading: boolean = true): Promise<Observable<NotificationCountResponse>> {
    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('User ID not found');
    }

    const url = `${environment.apiBaseUrl}${environment.endpoints.notifications_unread_count.replace('{user_id}', userId)}`;

    // Only log for manual calls, not for polling to reduce console noise
    if (showLoading) {
      console.info('Getting unread count from:', url);
    }

    return this.http.get<NotificationCountResponse>(url);
  }

  /**
   * Mark notifications as read
   */
  async markNotificationsAsRead(notificationIds: string[]): Promise<Observable<MarkReadResponse>> {
    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('User ID not found');
    }

    const url = `${environment.apiBaseUrl}${environment.endpoints.notifications_mark_read.replace('{user_id}', userId)}`;
    const payload: MarkReadRequest = {
      notification_ids: notificationIds
    };

    return this.http.post<MarkReadResponse>(url, payload);
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(notificationIds: string[]): Promise<Observable<MarkReadResponse>> {
    return this.markNotificationsAsRead(notificationIds);
  }

  /**
   * Update the unread count in the subject
   */
  updateUnreadCount(count: number): void {
    this.unreadCountSubject.next(count);
  }

  /**
   * Get current unread count value
   */
  getCurrentUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  /**
   * Refresh unread count from API (used for polling - no loading indicators)
   */
  async refreshUnreadCount(): Promise<void> {
    try {
      // Pass false to disable loading indicators for polling
      const countObservable = await this.getUnreadCount(false);
      countObservable.subscribe({
        next: (response) => {
          if (response.status && response.data) {
            this.updateUnreadCount(response.data.unread_count);
          }
        },
        error: (error) => {
          // Reduce console noise for polling errors
          if (console.groupCollapsed) {
            console.groupCollapsed('Notification polling error');
            console.error('Error refreshing unread count:', error);
            console.groupEnd();
          }
        }
      });
    } catch (error) {
      // Reduce console noise for polling errors
      if (console.groupCollapsed) {
        console.groupCollapsed('Notification polling error');
        console.error('Error getting unread count:', error);
        console.groupEnd();
      }
    }
  }

  /**
   * Refresh notifications from API and update the subject (used for polling - no loading indicators)
   * @param page - Page number to fetch (default: 1)
   * @param pageSize - Number of items per page (default: 50)
   * @param status - Filter by notification status (optional: 'read', 'unread')
   */
  async refreshNotifications(page: number = 1, pageSize: number = 50, status?: string): Promise<void> {
    try {
      // Pass false to disable loading indicators for polling
      const notificationsObservable = await this.getNotifications(false, page, pageSize, status);
      notificationsObservable.subscribe({
        next: (response) => {
          if (response?.status && response?.data?.notifications) {
            this.notificationsSubject.next(response.data.notifications);
            
            // Also update unread count based on API response or notifications
            if (response.data.unread_count !== undefined) {
              this.updateUnreadCount(response.data.unread_count);
            } else {
              const unreadCount = response.data.notifications.filter(n => !n.is_read).length;
              this.updateUnreadCount(unreadCount);
            }
          }
        },
        error: (error) => {
          // Reduce console noise for polling errors
          if (console.groupCollapsed) {
            console.groupCollapsed('Notification polling error');
            console.error('Error refreshing notifications:', error);
            console.groupEnd();
          }
        }
      });
    } catch (error) {
      // Reduce console noise for polling errors
      if (console.groupCollapsed) {
        console.groupCollapsed('Notification polling error');
        console.error('Error getting notifications:', error);
        console.groupEnd();
      }
    }
  }

  /**
   * Get current notifications value
   */
  getCurrentNotifications(): NotificationData[] {
    return this.notificationsSubject.value;
  }

  /**
   * Check if there are new notifications since last check
   */
  hasNewNotifications(previousNotifications: NotificationData[]): boolean {
    const currentNotifications = this.getCurrentNotifications();

    // If we have more notifications than before, there are new ones
    if (currentNotifications.length > previousNotifications.length) {
      return true;
    }

    // Check if any notification that was read is now unread (shouldn't happen but just in case)
    // or if there are any new notification IDs
    const previousIds = new Set(previousNotifications.map(n => n.id));
    return currentNotifications.some(notification => !previousIds.has(notification.id));
  }

  /**
   * Start polling for full notifications (called by notifications component)
   */
  startNotificationsPolling(): void {
    if (this.notificationsPollingSubscription) {
      return; // Already polling
    }

    // Initial load
    this.refreshNotifications();

    // Start polling every 10 seconds
    this.isPollingActive = true;
    this.notificationsPollingSubscription = interval(this.pollingInterval).subscribe(() => {
      if (this.isPollingActive && !document.hidden) {
        this.refreshNotifications();
      }
    });
  }

  /**
   * Start polling for unread count only (called by header component)
   */
  startCountPolling(): void {
    if (this.countPollingSubscription) {
      return; // Already polling
    }

    // Initial load
    this.refreshUnreadCount();

    // Start polling every 10 seconds
    this.isPollingActive = true;
    this.countPollingSubscription = interval(this.pollingInterval).subscribe(() => {
      if (this.isPollingActive && !document.hidden) {
        this.refreshUnreadCount();
      }
    });
  }

  /**
   * Stop all polling
   */
  stopPolling(): void {
    if (this.notificationsPollingSubscription) {
      this.notificationsPollingSubscription.unsubscribe();
      this.notificationsPollingSubscription = null;
    }

    if (this.countPollingSubscription) {
      this.countPollingSubscription.unsubscribe();
      this.countPollingSubscription = null;
    }

    this.isPollingActive = false;
  }

  /**
   * Setup listener for page visibility changes
   */
  private setupVisibilityChangeListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.isPollingActive = false;
      } else {
        this.isPollingActive = true;
        // Immediately check for notifications when page becomes visible
        if (this.notificationsPollingSubscription) {
          this.refreshNotifications();
        }
        if (this.countPollingSubscription) {
          this.refreshUnreadCount();
        }
      }
    });
  }
}
